// src/firebase/clubs.ts
import { collection, doc, getDocs, addDoc, deleteDoc, query, orderBy, updateDoc, where, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./config";
import { withCache, invalidateCache, invalidateCachePrefix } from "../utils/cache";

const COLLECTION = "clubs";

// ── Cache TTL ─────────────────────────────────────────────────────────────────
const TTL_ALL_CLUBS = 5 * 60 * 1000; // 5 minutes

export interface Club {
  id: string;
  name: string;
  imageUrl?: string;
  [key: string]: any;
}

/**
 * Returns all clubs ordered by name.
 * Result is cached for TTL_ALL_CLUBS (5 min) per session.
 * Both ClubRankings.tsx and RankingsPage.tsx benefit from this automatically.
 */
export const getAllClubs = async (): Promise<Club[]> => {
  return withCache("clubs:all", TTL_ALL_CLUBS, async () => {
    const q = query(collection(db, COLLECTION), orderBy("name"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Club));
  });
};

export const addClub = async (clubData: object) => {
  const result = await addDoc(collection(db, COLLECTION), clubData);
  invalidateCache("clubs:all");
  return result;
};

export const deleteClub = async (docId: string) => {
  const result = await deleteDoc(doc(db, COLLECTION, docId));
  invalidateCache("clubs:all");
  return result;
};

export const uploadClubImage = async (clubId: string, file: File): Promise<string> => {
  const storageRef = ref(storage, `clubs/${clubId}/logo`);
  await uploadBytes(storageRef, file, { cacheControl: "public, max-age=2592000, immutable" });
  return await getDownloadURL(storageRef);
};

export const updateClubImageUrl = async (clubId: string, imageUrl: string): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, clubId), { imageUrl });
  invalidateCache("clubs:all");
};

export const updateClubName = async (clubId: string, newName: string): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, clubId), { name: newName });
  invalidateCache("clubs:all");
};

/**
 * Propagates a club rename to all dependent collections:
 *   - rankingResults.clubName
 *   - registrations.club
 *
 * Safe to re-run: queries are based on old name, so already-updated docs
 * are not found again. Returns counts of updated documents.
 */
export const propagateClubRename = async (
  oldName: string,
  newName: string
): Promise<{ rankingResultsUpdated: number; registrationsUpdated: number }> => {
  const CHUNK = 499;

  // 1. Query rankingResults where clubName == oldName
  const rrSnap = await getDocs(
    query(collection(db, "rankingResults"), where("clubName", "==", oldName))
  );

  // 2. Query registrations where club == oldName
  const regSnap = await getDocs(
    query(collection(db, "registrations"), where("club", "==", oldName))
  );

  // 3. Batch update rankingResults
  const rrDocs = rrSnap.docs;
  for (let i = 0; i < rrDocs.length; i += CHUNK) {
    const batch = writeBatch(db);
    rrDocs.slice(i, i + CHUNK).forEach((d) => batch.update(d.ref, { clubName: newName }));
    await batch.commit();
  }

  // 4. Batch update registrations
  const regDocs = regSnap.docs;
  for (let i = 0; i < regDocs.length; i += CHUNK) {
    const batch = writeBatch(db);
    regDocs.slice(i, i + CHUNK).forEach((d) => batch.update(d.ref, { club: newName }));
    await batch.commit();
  }

  // 5. Cache invalidation
  invalidateCache("clubs:all");
  invalidateCachePrefix("rankingSnapshot:");
  invalidateCachePrefix("clubRankingSnapshot:");
  invalidateCachePrefix("snapshotKeys:");

  return {
    rankingResultsUpdated: rrDocs.length,
    registrationsUpdated: regDocs.length,
  };
};

/**
 * Scans all rankingResults and creates any club that is referenced
 * but hasn't been added to the clubs collection yet.
 * Returns an array of club names that were created.
 */
export const syncMissingClubs = async (): Promise<string[]> => {
  const rrSnap = await getDocs(collection(db, "rankingResults"));
  const existingClubNames = new Set<string>();
  rrSnap.docs.forEach((d) => {
    const name = d.data().clubName;
    if (name && typeof name === "string") existingClubNames.add(name.trim());
  });

  // Also check registrations
  const regSnap = await getDocs(collection(db, "registrations"));
  regSnap.docs.forEach((d) => {
    const club = d.data().club;
    if (club && typeof club === "string") existingClubNames.add(club.trim());
  });

  // Get current clubs
  const currentClubs = await getAllClubs();
  const currentNames = new Set(currentClubs.map((c) => c.name));

  const missingNames = [...existingClubNames].filter((n) => !currentNames.has(n));

  if (missingNames.length === 0) return [];

  // Create missing clubs in batches
  const CHUNK = 499;
  for (let i = 0; i < missingNames.length; i += CHUNK) {
    const batch = writeBatch(db);
    missingNames.slice(i, i + CHUNK).forEach((name) => {
      const ref = doc(collection(db, "clubs"));
      batch.set(ref, { name });
    });
    await batch.commit();
  }

  invalidateCache("clubs:all");
  return missingNames;
};